import { inject, Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { from, mergeMap, Observable } from 'rxjs';
import {
  START_ROUND,
  TIERS,
  ROOMS,
  WAITING_OPPONENTS,
  PRACTICE_ROUNDS,
  TOURNAMENT_PUBLISHED,
  TOURNAMENTS,
  MINI_LEADERBOARD,
  LEADERBOARD,
  USERS,
  INVENTORY,
  SPIN_DAILY_SPINNER,
  SPIN_ROUND_SPINNER,
} from '../api/wager-api';
import { getAuth, User } from 'firebase/auth';
import { OperatorService } from '../services/operator/operator.service';
import { SignedOutError } from 'src/app/shared/errors/signed-out-error';
import { DEALER_ENDPOINTS } from '../api/dealer/dealer-api';
import { AnalyticsService } from '../services/analytics/analytics.service';
import { JIT_ENDPOINTS } from '../api/jit/jit-api';
import { WAGER_ENDPOINTS } from '../api/wager/wager-api';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private analyticsService = inject(AnalyticsService);

  private readonly authenticatedUrls = [
    DEALER_ENDPOINTS.CHECKPOINTS,
    DEALER_ENDPOINTS.GAME_STATES,
    JIT_ENDPOINTS.SET_LIGHTNING_ADDRESS,
    JIT_ENDPOINTS.CREATE_INVOICE,
    JIT_ENDPOINTS.DEPOSIT_STATUS,
    WAGER_ENDPOINTS.ACTIVITY_FEED,
    WAGER_ENDPOINTS.FIND_MATCH,
    WAGER_ENDPOINTS.FINISH_ROUND,
    WAGER_ENDPOINTS.GAME_LEADERBOARDS,
    START_ROUND,
    TIERS,
    ROOMS,
    WAITING_OPPONENTS,
    PRACTICE_ROUNDS,
    TOURNAMENT_PUBLISHED,
    TOURNAMENTS,
    MINI_LEADERBOARD,
    LEADERBOARD,
    USERS,
    INVENTORY,
    SPIN_DAILY_SPINNER,
    SPIN_ROUND_SPINNER,
  ];

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (OperatorService.IsDemo || !this.requiresAuth(request.url)) {
      return next.handle(request);
    }

    return from(this.tryGetUser(request.url)).pipe(
      mergeMap((user) => from(this.tryGetToken(user, request.url))),
      mergeMap((token) => {
        const authRequest = token
          ? request.clone({
              setHeaders: { Authorization: `Bearer ${token}` },
            })
          : request;
        return next.handle(authRequest);
      }),
    );
  }

  private requiresAuth(url: string): boolean {
    return this.authenticatedUrls.some((endpoint) => url.includes(endpoint));
  }

  private async tryGetUser(url: string): Promise<User> {
    const auth = getAuth();

    for (let attempt = 0; attempt < 3; attempt++) {
      const user = auth.currentUser;
      if (user) {
        return user;
      }

      this.analyticsService.logBreadcrumb(`auth.interceptor: Failed get user, attempt: ${attempt}`);

      const delay = 100 * (1 << attempt);
      await this.sleep(delay);
    }

    // Last attempt after backoff loop
    const finalUser = auth.currentUser;
    if (finalUser) {
      return finalUser;
    }
    this.analyticsService.logBreadcrumb(`auth.interceptor: User signed out from: ${url}`);
    throw new SignedOutError(`User signed out from: ${url}`, url);
  }

  private async tryGetToken(user: User, url: string): Promise<string> {
    const tokenResult = await user.getIdTokenResult();
    const expiresAt = new Date(tokenResult.expirationTime).getTime();
    const timeRemaining = (expiresAt - Date.now()) / 1000 / 60;
    this.analyticsService.logBreadcrumb(
      `auth.interceptor: Get IdToken: ${tokenResult.issuedAtTime}, remaining: ${timeRemaining}, url: ${url}`,
    );
    return tokenResult.token;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
